import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography,
  Box, FormControlLabel, Checkbox, alpha, CircularProgress, IconButton, Chip, InputAdornment,
} from '@mui/material';
import {
  MyLocation as MyLocationIcon, Close as CloseIcon, PlaceOutlined as PlaceIcon,
  OpenInNew as OpenInNewIcon, Straighten as StraightenIcon,
} from '@mui/icons-material';
import api from '../../../services/api';
import { C, R, FONT_UI, inputSx } from './AttendanceSharedUI';

const FaceAttendanceLocationDialog = ({ open, onClose, onSaved, onError }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [form, setForm] = useState({ latitude: '', longitude: '', radius_m: '120', enforce_location: true });
  const [locationAccuracyM, setLocationAccuracyM] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLocationAccuracyM(null);
        const { data } = await api.get('/face/attendance-location');
        if (cancelled) return;
        setForm({
          latitude: String(data.latitude ?? ''), longitude: String(data.longitude ?? ''),
          radius_m: String(data.radius_m ?? 120), enforce_location: Boolean(data.enforce_location ?? true),
        });
      } catch (err) {
        if (!cancelled) onError?.(err?.response?.data?.message || 'Failed to load face check-in location.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, onError]);

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      onError?.('Geolocation is not supported in this browser.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const accuracy = typeof pos.coords.accuracy === 'number' ? pos.coords.accuracy : null;
        const suggestedRadius = accuracy ? Math.max(120, Math.ceil(accuracy * 2)) : 120;
        setLocationAccuracyM(accuracy);
        setForm((prev) => ({
          ...prev, latitude: String(pos.coords.latitude), longitude: String(pos.coords.longitude),
          radius_m: String(Number.isNaN(parseFloat(prev.radius_m)) ? suggestedRadius : Math.max(parseFloat(prev.radius_m), suggestedRadius)),
        }));
        setLocating(false);
      },
      () => { setLocating(false); onError?.('Could not read your current location. Allow location access and try again.'); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const handleSave = async () => {
    const latitude = parseFloat(form.latitude);
    const longitude = parseFloat(form.longitude);
    const radius_m = parseFloat(form.radius_m);
    if (Number.isNaN(latitude) || Number.isNaN(longitude)) { onError?.('Latitude and longitude are required.'); return; }
    if (Number.isNaN(radius_m) || radius_m < 10) { onError?.('Allowed radius must be at least 10 meters.'); return; }
    try {
      setSaving(true);
      const { data } = await api.put('/face/attendance-location', { latitude, longitude, radius_m, enforce_location: form.enforce_location });
      onSaved?.(data?.message || 'Face check-in location updated.');
      onClose?.();
    } catch (err) {
      onError?.(err?.response?.data?.message || 'Failed to update face check-in location.');
    } finally { setSaving(false); }
  };

  const hasLocation = Boolean(form.latitude && form.longitude);
  const mapsUrl = hasLocation ? `https://www.google.com/maps?q=${encodeURIComponent(form.latitude)},${encodeURIComponent(form.longitude)}` : null;
  const sectionSx = { p: { xs: 2, sm: 2.25 }, borderRadius: R.lg, bgcolor: alpha(C.bg2, 0.5), border: `1px solid ${C.border}` };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: {
      borderRadius: R.lg, background: `linear-gradient(160deg, ${alpha(C.bg1, 0.98)} 0%, ${alpha(C.bg0, 0.99)} 100%)`,
      border: `1px solid ${C.border}`, boxShadow: '0 24px 80px rgba(0,0,0,0.5)', colorScheme: 'dark',
    } }}>
      <DialogTitle sx={{ px: { xs: 2.5, sm: 3 }, pt: 2.75, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography sx={{ color: C.textPrimary, fontFamily: FONT_UI, fontWeight: 700, fontSize: 22 }}>Change check-in location</Typography>
            <Typography sx={{ color: C.textSecondary, fontFamily: FONT_UI, fontSize: 13, mt: 0.5 }}>Members must be inside this area to check in.</Typography>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} disabled={saving} size="small" sx={{ color: C.textSecondary, mt: -0.5 }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{
        px: { xs: 2.5, sm: 3 }, pt: '16px !important',
        scrollbarColor: `${C.textMuted} transparent`,
        '&::-webkit-scrollbar': { width: 8 },
        '&::-webkit-scrollbar-thumb': { bgcolor: C.textMuted, borderRadius: 8 },
      }}>
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 7 }}><CircularProgress size={32} sx={{ color: C.blue }} /></Box> : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={sectionSx}>
              <Typography sx={{ color: C.textPrimary, fontFamily: FONT_UI, fontWeight: 700, fontSize: 14 }}>1. Set the centre point</Typography>
              <Typography sx={{ color: C.textSecondary, fontFamily: FONT_UI, fontSize: 12.5, mt: 0.5, mb: 1.75 }}>For best accuracy, open this on the check-in phone and stand at the venue.</Typography>
              <Button fullWidth variant="contained" size="large" startIcon={(locating || saving) ? <CircularProgress size={18} color="inherit" /> : <MyLocationIcon />}
                onClick={handleUseCurrentLocation} disabled={locating || saving}
                sx={{
                  textTransform: 'none', fontFamily: FONT_UI, fontWeight: 700, borderRadius: R.md, py: 1.15,
                  color: '#fff', bgcolor: C.blue, '&:hover': { bgcolor: C.blueDim },
                  '&.Mui-disabled': {
                    color: alpha('#fff', 0.72), bgcolor: alpha(C.blue, 0.38),
                    boxShadow: 'none',
                  },
                }}>
                {locating ? 'Finding your location…' : saving ? 'Saving changes…' : hasLocation ? 'Update to my current location' : 'Use my current location'}
              </Button>
              {hasLocation && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, color: C.textSecondary }}>
                <PlaceIcon sx={{ fontSize: 18, color: C.green }} />
                <Typography sx={{ fontSize: 12.5, fontFamily: FONT_UI, flex: 1 }}>Centre set{locationAccuracyM != null ? ` · GPS accuracy about ${Math.round(locationAccuracyM)} m` : ''}</Typography>
                <Button component="a" href={mapsUrl} target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewIcon />} size="small"
                  sx={{ textTransform: 'none', fontFamily: FONT_UI, minWidth: 0, px: 0.5 }}>View map</Button>
              </Box>}
              {locationAccuracyM > 50 && <Typography sx={{ mt: 1, fontSize: 12, color: C.amber, fontFamily: FONT_UI }}>GPS accuracy is low. Try again outdoors or choose a wider radius below.</Typography>}
            </Box>

            <Box sx={sectionSx}>
              <Typography sx={{ color: C.textPrimary, fontFamily: FONT_UI, fontWeight: 700, fontSize: 14 }}>2. Choose the allowed distance</Typography>
              <Typography sx={{ color: C.textSecondary, fontFamily: FONT_UI, fontSize: 12.5, mt: 0.5, mb: 1.5 }}>Members within this distance from the centre can check in.</Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                {[100, 250, 500, 1000].map((radius) => <Chip key={radius} label={`${radius} m`} clickable
                  onClick={() => setForm((p) => ({ ...p, radius_m: String(radius) }))} color={Number(form.radius_m) === radius ? 'primary' : 'default'}
                  variant={Number(form.radius_m) === radius ? 'filled' : 'outlined'} sx={{
                    fontFamily: FONT_UI, fontWeight: 600,
                    color: Number(form.radius_m) === radius ? '#fff' : C.textPrimary,
                    bgcolor: Number(form.radius_m) === radius ? C.blue : alpha(C.bg0, 0.18),
                    borderColor: Number(form.radius_m) === radius ? C.blue : C.border2,
                    '&:hover': { bgcolor: Number(form.radius_m) === radius ? C.blueDim : alpha(C.blue, 0.1), borderColor: alpha(C.blue, 0.5) },
                    '&:focus-visible': { outline: `2px solid ${alpha(C.blue, 0.8)}`, outlineOffset: 2 },
                  }} />)}
              </Box>
              <TextField fullWidth label="Custom distance" type="number" inputProps={{ min: 10, max: 5000, step: 1 }} value={form.radius_m}
                onChange={(e) => setForm((p) => ({ ...p, radius_m: e.target.value }))} sx={inputSx}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><StraightenIcon sx={{ fontSize: 18, color: C.textSecondary }} /></InputAdornment>,
                  endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { color: C.textSecondary } }}>meters</InputAdornment>,
                }} />
            </Box>

            <FormControlLabel sx={{ ...sectionSx, m: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { flex: 1 } }}
              control={<Checkbox checked={form.enforce_location} onChange={(e) => setForm((p) => ({ ...p, enforce_location: e.target.checked }))} sx={{ color: alpha(C.blue, 0.6), '&.Mui-checked': { color: C.blue }, pt: 0.1 }} />}
              label={<Box sx={{ pt: 0.1 }}><Typography sx={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, fontFamily: FONT_UI }}>Enforce this location</Typography>
                <Typography sx={{ fontSize: 12.5, color: C.textSecondary, fontFamily: FONT_UI, mt: 0.35, lineHeight: 1.45 }}>Block face check-in when a member is outside the allowed distance.</Typography></Box>} />

            <Box component="details" sx={{ color: C.textSecondary, fontFamily: FONT_UI, fontSize: 12.5, px: 0.5, '& summary': { cursor: 'pointer' } }}>
              <summary>Advanced: edit coordinates manually</summary>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField fullWidth label="Latitude" value={form.latitude} onChange={(e) => setForm((p) => ({ ...p, latitude: e.target.value }))} sx={inputSx} />
                <TextField fullWidth label="Longitude" value={form.longitude} onChange={(e) => setForm((p) => ({ ...p, longitude: e.target.value }))} sx={inputSx} />
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5, borderTop: `1px solid ${C.border}`, mt: 1, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} sx={{
          color: C.textSecondary, fontFamily: FONT_UI, textTransform: 'none', fontWeight: 600,
          '&:hover': { color: C.textPrimary, bgcolor: alpha(C.textPrimary, 0.06) },
        }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || saving || !hasLocation}
          sx={{
            fontFamily: FONT_UI, textTransform: 'none', fontWeight: 700, px: 2.5, borderRadius: R.md,
            color: '#fff', bgcolor: C.blue, '&:hover': { bgcolor: C.blueDim },
            '&.Mui-disabled': { color: C.textSecondary, bgcolor: alpha(C.textPrimary, 0.08), border: `1px solid ${C.border}` },
          }}>{saving ? 'Saving…' : 'Save changes'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FaceAttendanceLocationDialog;
